/**
 * Unified per-game lifecycle definitions.
 *
 * Each game declares which statuses it uses and the legal transitions between them.
 * Setup phases are monotonic (one-way); the runtime portion cycles through
 * `lobby → active → round_ending → lobby` while rounds run; `completed` and
 * `abandoned` are terminal sinks.
 *
 *   FPR:  draft → pre_registration → pool_creation → team_creation → pre_game → lobby → active → round_ending → lobby → completed/abandoned
 *   HNS:  draft → pre_registration                                              → lobby → active → round_ending → lobby → completed/abandoned
 *   TRL:  draft → pre_registration                                              → lobby → active → round_ending → completed/abandoned
 *   HNST: draft → pre_registration                  → team_creation             → lobby → active → round_ending → lobby → completed/abandoned
 *
 * Backend gating, frontend transition UI, and overlay rendering all read from here.
 */
import { GameType } from '../constants/gameTypes';
const TERMINAL = ['completed', 'abandoned'];
const FPR_LIFECYCLE = {
    statuses: [
        'draft',
        'pre_registration',
        'pool_creation',
        'team_creation',
        'pre_game',
        'lobby',
        'active',
        'round_ending',
        'completed',
        'abandoned',
    ],
    initial: 'draft',
    transitions: {
        draft: ['pre_registration', 'pool_creation', 'abandoned'],
        pre_registration: ['draft', 'pool_creation', 'abandoned'],
        pool_creation: ['pre_registration', 'team_creation', 'abandoned'],
        team_creation: ['pool_creation', 'pre_game', 'abandoned'],
        pre_game: ['lobby', 'abandoned'],
        lobby: ['active', 'completed', 'abandoned'],
        active: ['round_ending', 'lobby', 'completed', 'abandoned'],
        round_ending: ['lobby', 'completed', 'abandoned'],
        completed: [],
        abandoned: [],
    },
    terminal: TERMINAL,
    roundEndingDurationMs: 8000,
};
const HNS_LIFECYCLE = {
    statuses: ['draft', 'pre_registration', 'lobby', 'active', 'round_ending', 'completed', 'abandoned'],
    initial: 'draft',
    transitions: {
        draft: ['pre_registration', 'lobby', 'abandoned'],
        pre_registration: ['draft', 'lobby', 'abandoned'],
        // Setup phases unused by HNS — kept in the record for type completeness.
        pool_creation: [],
        team_creation: [],
        pre_game: [],
        lobby: ['active', 'completed', 'abandoned'],
        active: ['round_ending', 'lobby', 'completed', 'abandoned'],
        round_ending: ['lobby', 'completed', 'abandoned'],
        completed: [],
        abandoned: [],
    },
    terminal: TERMINAL,
    roundEndingDurationMs: 5000,
};
const TRL_LIFECYCLE = {
    // Like HNS, Trailblazers has an optional Discord-only pre_registration phase for
    // collecting sign-ups (draft → pre_registration → lobby). Registration is
    // informational and never gates a transition. Teams are still created by the
    // admin during the lobby phase.
    //
    // Unlike HNS/FPR, TRL is NOT round-cyclic: the whole game is one long `active`
    // phase over the galaxy map (lobby → active = "start game / reveal start
    // points"). There is no mid-game bounce back to lobby — teams progress
    // independently until everyone finishes, then active → round_ending →
    // completed shows the final scoresheet.
    statuses: ['draft', 'pre_registration', 'lobby', 'active', 'round_ending', 'completed', 'abandoned'],
    initial: 'draft',
    transitions: {
        draft: ['pre_registration', 'lobby', 'abandoned'],
        pre_registration: ['draft', 'lobby', 'abandoned'],
        // Setup phases unused by TRL — kept in the record for type completeness.
        pool_creation: [],
        team_creation: [],
        pre_game: [],
        lobby: ['active', 'completed', 'abandoned'],
        active: ['round_ending', 'completed', 'abandoned'],
        round_ending: ['completed', 'abandoned'],
        completed: [],
        abandoned: [],
    },
    terminal: TERMINAL,
    roundEndingDurationMs: 8000,
};
const HNST_LIFECYCLE = {
    // Like HNS with a team phase first: in `team_creation` the admin sets the two
    // Team Captains and fills the teams (by hand or by the captains' snake draft).
    // Entering `lobby` locks the teams and creates their voice channels; the
    // rounds then cycle like HNS.
    statuses: ['draft', 'pre_registration', 'team_creation', 'lobby', 'active', 'round_ending', 'completed', 'abandoned'],
    initial: 'draft',
    transitions: {
        draft: ['pre_registration', 'team_creation', 'abandoned'],
        pre_registration: ['draft', 'team_creation', 'abandoned'],
        team_creation: ['pre_registration', 'lobby', 'abandoned'],
        // Setup phases unused by HNST — kept in the record for type completeness.
        pool_creation: [],
        pre_game: [],
        lobby: ['active', 'completed', 'abandoned'],
        active: ['round_ending', 'lobby', 'completed', 'abandoned'],
        round_ending: ['lobby', 'completed', 'abandoned'],
        completed: [],
        abandoned: [],
    },
    terminal: TERMINAL,
    roundEndingDurationMs: 5000,
};
export const GAME_LIFECYCLES = {
    [GameType.FP_RACE]: FPR_LIFECYCLE,
    [GameType.HIDE_AND_SEEK]: HNS_LIFECYCLE,
    [GameType.TRAILBLAZERS]: TRL_LIFECYCLE,
    [GameType.HIDE_AND_SEEK_TEAMS]: HNST_LIFECYCLE,
};
export function getLifecycle(gameType) {
    return GAME_LIFECYCLES[gameType];
}
export function canTransition(gameType, from, to) {
    const lifecycle = GAME_LIFECYCLES[gameType];
    if (!lifecycle)
        return false;
    return lifecycle.transitions[from]?.includes(to) ?? false;
}
export function getAvailableTransitions(gameType, from) {
    return GAME_LIFECYCLES[gameType]?.transitions[from] ?? [];
}
export function isTerminal(gameType, status) {
    return GAME_LIFECYCLES[gameType]?.terminal.includes(status) ?? false;
}
/**
 * True if the status is part of the cyclic runtime portion of the game
 * (lobby / active / round_ending). Useful when callers need to distinguish
 * "game is in setup" vs "game is running".
 */
export function isRuntimeStatus(status) {
    return status === 'lobby' || status === 'active' || status === 'round_ending';
}
